import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { FikoytKok3DElement } from '../fikoyt-kok-3d-element';
import { FikoSahne } from '../../FikoSahne';

// Global TWEEN referansı
declare var TWEEN: any;

// ============================================================================
// TİPLER VE ARAYÜZLER
// ============================================================================

/** Çizgi stili */
export type PolylineStili = 'duz' | 'kesikli' | 'noktali' | 'kesikli-noktali';

/** Çizgi tipi */
export type PolylineTipi = 'Line2' | 'Line' | 'LineSegments';

/** Köşe noktası tipi */
export type KoseNoktasiTipi = 'keskin' | 'yumusak' | 'bezier';

/** Polyline ayarları */
export interface IPolylineAyarlari {
    renk?: number | string;
    kalinlik?: number;
    stil?: PolylineStili;
    kesikliAralik?: number;
    kesikliBoyut?: number;
    opaklık?: number;
    secilebilir?: boolean;
    katman?: number;
    cizgiTipi?: PolylineTipi;
    enUstte?: boolean;
    renderEt?: boolean;
    kapali?: boolean;  // Polyline kapalı mı (ilk ve son nokta birleşik)
    noktaGoster?: boolean;  // Köşe noktalarını göster
    noktaBoyutu?: number;  // Köşe noktası boyutu
    noktaRengi?: number | string;  // Köşe noktası rengi
}

/** Varsayılan polyline ayarları */
const VARSAYILAN_AYARLAR: IPolylineAyarlari = {
    renk: 0x00ffff,  // Cyan - FikoYT tema rengi
    kalinlik: 2,
    stil: 'duz',
    kesikliAralik: 5,
    kesikliBoyut: 10,
    opaklık: 1,
    secilebilir: true,
    katman: 0,
    cizgiTipi: 'Line2',
    enUstte: false,
    renderEt: true,
    kapali: false,
    noktaGoster: true,
    noktaBoyutu: 4,
    noktaRengi: 0xffff00  // Sarı noktalar
};

// ============================================================================
// HANDLE SİSTEMİ (Polyline Vertex Handles)
// ============================================================================

/**
 * Polyline köşe noktaları için sürüklenebilir handle'lar
 * THREE.Points kullanarak zoom'dan bağımsız sabit boyutlu noktalar
 */
export class FikoYT3dPolylineHandles extends THREE.Group {
    /** Bağlı polyline referansı */
    private polyline: FikoYT3dPolyline;

    /** Points objesi - tüm handle noktaları */
    private _points: THREE.Points | null = null;

    /** Points geometrisi */
    private _geometry: THREE.BufferGeometry | null = null;

    /** Points materyali */
    private _material: THREE.PointsMaterial | null = null;

    /** Sürükleme durumu */
    isDragging: boolean = false;

    /** Hangi handle sürükleniyor (index) */
    dragTarget: number | null = null;

    /** Handle boyutu (pixel) */
    private handleBoyut: number = 12;

    /** Hit radius for raycast (world units) */
    private hitRadius: number = 8;

    /** Hover edilen handle index */
    private _hoverIndex: number = -1;

    /** Değişiklik callback'i */
    onChange?: (noktalar: THREE.Vector3[]) => void;

    /** Eski API uyumluluğu için boş array */
    private _handleler: THREE.Object3D[] = [];

    constructor(polyline: FikoYT3dPolyline) {
        super();
        this.polyline = polyline;
        this.name = 'PolylineHandles_' + polyline.uuid;

        // Handle boyutunu kalınlığa göre ayarla
        this.handleBoyut = Math.max(10, Math.min(16, polyline.kalinlik * 3));
        this.hitRadius = Math.max(5, polyline.kalinlik * 2);

        // Points oluştur
        this.pointsOlustur();

        // Render order - her zaman üstte
        this.renderOrder = 1000;
    }

    /** Handle getter - eski API uyumluluğu */
    get handleler(): THREE.Object3D[] { return this._handleler; }

    /**
     * Points objesini oluştur
     */
    private pointsOlustur(): void {
        // Mevcut points'i temizle
        this.handleTemizle();

        const noktalar = this.polyline.noktalar;
        const noktaSayisi = noktalar.length;

        if (noktaSayisi === 0) return;

        // Geometry oluştur
        this._geometry = new THREE.BufferGeometry();

        // Pozisyonlar
        const positions = new Float32Array(noktaSayisi * 3);
        for (let i = 0; i < noktaSayisi; i++) {
            positions[i * 3] = noktalar[i].x;
            positions[i * 3 + 1] = noktalar[i].y;
            positions[i * 3 + 2] = noktalar[i].z;
        }
        this._geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

        // Renkler - başlangıç mavi, bitiş kırmızı, ara sarı
        const colors = new Float32Array(noktaSayisi * 3);
        for (let i = 0; i < noktaSayisi; i++) {
            let r: number, g: number, b: number;
            if (i === 0) {
                // Mavi - başlangıç
                r = 0x34 / 255; g = 0x98 / 255; b = 0xdb / 255;
            } else if (i === noktaSayisi - 1 && !this.polyline.kapali) {
                // Kırmızı - bitiş
                r = 0xe7 / 255; g = 0x4c / 255; b = 0x3c / 255;
            } else {
                // Sarı - ara noktalar
                r = 0xf1 / 255; g = 0xc4 / 255; b = 0x0f / 255;
            }
            colors[i * 3] = r;
            colors[i * 3 + 1] = g;
            colors[i * 3 + 2] = b;
        }
        this._geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

        // Material - sizeAttenuation: false ile zoom'dan bağımsız
        this._material = new THREE.PointsMaterial({
            size: this.handleBoyut,
            sizeAttenuation: false,  // Zoom'dan etkilenmez!
            vertexColors: true,
            depthTest: false,
            depthWrite: false,
            transparent: true,
            opacity: 0.9
        });

        // Points objesi
        this._points = new THREE.Points(this._geometry, this._material);
        this._points.name = 'PolylineHandlePoints';
        this._points.frustumCulled = false;
        this._points.renderOrder = 1002;
        this._points.layers.enableAll();

        // Polyline referansı ekle
        this._points.userData['polylineHandles'] = this;
        this._points.userData['secilebilir'] = true;

        // Çift tıklama delegasyonu
        const polylineRef = this.polyline;
        (this._points as any).ciftTiklandi = () => {
            if (typeof polylineRef?.ciftTiklandi === 'function') {
                polylineRef.ciftTiklandi();
            }
        };

        this.add(this._points);
        this.updateMatrixWorld(true);
    }

    /**
     * Handle pozisyonlarını güncelle
     */
    guncelle(): void {
        if (!this.polyline) return;

        const noktalar = this.polyline.noktalar;

        // Nokta sayısı değiştiyse yeniden oluştur
        if (!this._geometry || !this._points) {
            this.pointsOlustur();
            return;
        }

        const posAttr = this._geometry.getAttribute('position') as THREE.BufferAttribute;
        if (posAttr.count !== noktalar.length) {
            this.pointsOlustur();
            return;
        }

        // Pozisyonları güncelle
        for (let i = 0; i < noktalar.length; i++) {
            posAttr.setXYZ(i, noktalar[i].x, noktalar[i].y, noktalar[i].z);
        }
        posAttr.needsUpdate = true;

        // Renkleri güncelle (kapali durumu değişmiş olabilir)
        const colorAttr = this._geometry.getAttribute('color') as THREE.BufferAttribute;
        for (let i = 0; i < noktalar.length; i++) {
            let r: number, g: number, b: number;
            if (i === 0) {
                r = 0x34 / 255; g = 0x98 / 255; b = 0xdb / 255;
            } else if (i === noktalar.length - 1 && !this.polyline.kapali) {
                r = 0xe7 / 255; g = 0x4c / 255; b = 0x3c / 255;
            } else {
                r = 0xf1 / 255; g = 0xc4 / 255; b = 0x0f / 255;
            }
            colorAttr.setXYZ(i, r, g, b);
        }
        colorAttr.needsUpdate = true;

        this.updateMatrixWorld(true);
    }

    /**
     * Hover efekti uygula (index ile)
     */
    setHover(handle: THREE.Object3D | null): void {
        // Points için hover efekti - boyut değişikliği yapılabilir
        // Şimdilik sadece index'i saklıyoruz
        if (handle && handle.userData['handleIndex'] !== undefined) {
            this._hoverIndex = handle.userData['handleIndex'];
        } else {
            this._hoverIndex = -1;
        }
    }

    /**
     * Hover edilen handle index'ini ayarla
     */
    setHoverIndex(index: number): void {
        this._hoverIndex = index;
        // Hover efekti için material size değiştirilebilir
        // Şimdilik basit tutuyoruz
    }

    /**
     * Raycast için targets döndür
     */
    getRaycastTargets(): THREE.Object3D[] {
        if (this._points) {
            return [this._points];
        }
        return [];
    }

    /**
     * Manuel küre kesişim testi - raycast için
     * Ekran koordinatlarından bağımsız, world space'de çalışır
     */
    manuelKureKesisim(ray: THREE.Ray): { hit: boolean; handleIndex: number | null; mesafe: number } {
        this.updateMatrixWorld(true);

        let enYakinMesafe = Infinity;
        let bulunanIndex: number | null = null;

        const noktalar = this.polyline.noktalar;

        for (let i = 0; i < noktalar.length; i++) {
            const sphere = new THREE.Sphere(noktalar[i], this.hitRadius);
            const intersect = new THREE.Vector3();

            if (ray.intersectSphere(sphere, intersect)) {
                const mesafe = ray.origin.distanceTo(intersect);
                if (mesafe < enYakinMesafe) {
                    enYakinMesafe = mesafe;
                    bulunanIndex = i;
                }
            }
        }

        return {
            hit: bulunanIndex !== null,
            handleIndex: bulunanIndex,
            mesafe: enYakinMesafe
        };
    }

    /**
     * Ekran koordinatlarına göre en yakın handle'ı bul
     * @param screenX Ekran X koordinatı (normalized -1 to 1)
     * @param screenY Ekran Y koordinatı (normalized -1 to 1)
     * @param kamera Aktif kamera
     * @param threshold Pixel cinsinden eşik değeri
     */
    noktadanHandleBul(
        screenX: number,
        screenY: number,
        kamera: THREE.Camera,
        threshold: number = 20
    ): { index: number; mesafe: number } | null {
        const noktalar = this.polyline.noktalar;
        let enYakinIndex = -1;
        let enYakinMesafe = Infinity;

        const tempVec = new THREE.Vector3();

        for (let i = 0; i < noktalar.length; i++) {
            // World position'ı screen space'e çevir
            tempVec.copy(noktalar[i]);
            tempVec.project(kamera);

            // Ekran mesafesi hesapla
            const dx = (tempVec.x - screenX) * 0.5;  // -1 to 1 range
            const dy = (tempVec.y - screenY) * 0.5;
            const screenDist = Math.sqrt(dx * dx + dy * dy);

            if (screenDist < enYakinMesafe) {
                enYakinMesafe = screenDist;
                enYakinIndex = i;
            }
        }

        // Threshold kontrolü (normalized space'de)
        // threshold pixel'i normalized'a çevir (yaklaşık)
        const normalizedThreshold = threshold / 500;  // Yaklaşık değer

        if (enYakinIndex >= 0 && enYakinMesafe < normalizedThreshold) {
            return { index: enYakinIndex, mesafe: enYakinMesafe };
        }

        return null;
    }

    /**
     * Handle'ları temizle
     */
    private handleTemizle(): void {
        if (this._points) {
            this.remove(this._points);
        }
        if (this._geometry) {
            this._geometry.dispose();
            this._geometry = null;
        }
        if (this._material) {
            this._material.dispose();
            this._material = null;
        }
        this._points = null;
        this._handleler = [];
    }

    /**
     * Kaynakları temizle
     */
    temizle(): void {
        this.handleTemizle();

        if (this.parent) {
            this.parent.remove(this);
        }
    }
}

// ============================================================================
// ANA SINIF
// ============================================================================

/**
 * FikoYT 3D Polyline Sınıfı
 *
 * Birden fazla noktayı birleştiren çoklu çizgi.
 * Noktalar arasında segment'ler oluşturur.
 * İsteğe bağlı olarak kapalı (polygon) yapılabilir.
 *
 * @extends FikoytKok3DElement
 */
export class FikoYT3dPolyline extends FikoytKok3DElement {
    /** Otomatik isimlendirme için sayaç */
    static SAYAC = 1;

    override type = 'FikoYT3dPolyline';

    // ========================================================================
    // ÖZELLİKLER
    // ========================================================================

    /** Köşe noktaları */
    private _noktalar: THREE.Vector3[] = [];

    /** Polyline ayarları */
    private _ayarlar: IPolylineAyarlari = { ...VARSAYILAN_AYARLAR };

    /** Three.js çizgi nesnesi */
    private _cizgi: Line2 | THREE.Line | THREE.LineSegments | null = null;

    /** Çizgi geometrisi */
    private _geometri: LineGeometry | THREE.BufferGeometry | null = null;

    /** Çizgi materyali */
    private _materyal: LineMaterial | THREE.LineBasicMaterial | THREE.LineDashedMaterial | null = null;

    /** Köşe noktası gösterge mesh'leri */
    private _noktaMeshleri: THREE.Mesh[] = [];

    /** Sahne referansı */
    sahne?: FikoSahne;

    /** Handle'lar */
    private _handles: FikoYT3dPolylineHandles | null = null;

    /** Sağ tıklama menüsü */
    public sagMenu: any[] = [];

    // ========================================================================
    // GETTER / SETTER
    // ========================================================================

    /** Köşe noktaları (kopya döndürür) */
    get noktalar(): THREE.Vector3[] {
        return this._noktalar.map(n => n.clone());
    }
    set noktalar(deger: THREE.Vector3[]) {
        this._noktalar = deger.map(n => n.clone());
        this.cizgiyiGuncelle();
    }

    /** Nokta sayısı */
    get noktaSayisi(): number {
        return this._noktalar.length;
    }

    /** Segment sayısı */
    get segmentSayisi(): number {
        if (this._noktalar.length < 2) return 0;
        return this._ayarlar.kapali
            ? this._noktalar.length
            : this._noktalar.length - 1;
    }

    /** Toplam uzunluk */
    get toplamUzunluk(): number {
        let uzunluk = 0;
        for (let i = 0; i < this._noktalar.length - 1; i++) {
            uzunluk += this._noktalar[i].distanceTo(this._noktalar[i + 1]);
        }
        if (this._ayarlar.kapali && this._noktalar.length > 2) {
            uzunluk += this._noktalar[this._noktalar.length - 1].distanceTo(this._noktalar[0]);
        }
        return uzunluk;
    }

    /** Ağırlık merkezi */
    get merkezNokta(): THREE.Vector3 {
        if (this._noktalar.length === 0) return new THREE.Vector3();

        const merkez = new THREE.Vector3();
        this._noktalar.forEach(n => merkez.add(n));
        merkez.divideScalar(this._noktalar.length);
        return merkez;
    }

    /** Bounding Box */
    get boundingBox(): THREE.Box3 {
        const box = new THREE.Box3();
        this._noktalar.forEach(n => box.expandByPoint(n));
        return box;
    }

    /** Kapalı mı */
    get kapali(): boolean { return this._ayarlar.kapali!; }
    set kapali(deger: boolean) {
        this._ayarlar.kapali = deger;
        this.cizgiyiGuncelle();
    }

    /** Renk */
    get renk(): number | string { return this._ayarlar.renk!; }
    set renk(deger: number | string) {
        this._ayarlar.renk = deger;
        this.materyaliGuncelle();
    }

    /** Kalınlık */
    get kalinlik(): number { return this._ayarlar.kalinlik!; }
    set kalinlik(deger: number) {
        this._ayarlar.kalinlik = deger;
        this.materyaliGuncelle();
    }

    /** Çizgi stili */
    get stil(): PolylineStili { return this._ayarlar.stil!; }
    set stil(deger: PolylineStili) {
        this._ayarlar.stil = deger;
        this.stilGuncelle();
    }

    /** Opaklık */
    get opaklık(): number { return this._ayarlar.opaklık!; }
    set opaklık(deger: number) {
        this._ayarlar.opaklık = Math.max(0, Math.min(1, deger));
        this.materyaliGuncelle();
    }

    /** Seçilebilirlik */
    get secilebilir(): boolean { return this._ayarlar.secilebilir!; }
    set secilebilir(deger: boolean) {
        this._ayarlar.secilebilir = deger;
        if (this._cizgi) {
            this._cizgi.userData['secilebilir'] = deger;
        }
    }

    /** Çizgi tipi */
    get cizgiTipi(): PolylineTipi { return this._ayarlar.cizgiTipi || 'Line2'; }
    set cizgiTipi(deger: PolylineTipi) {
        if (this._ayarlar.cizgiTipi === deger) return;
        this._ayarlar.cizgiTipi = deger;
        this.cizgiyiYenidenOlustur();
    }

    /** Nokta göster */
    get noktaGoster(): boolean { return this._ayarlar.noktaGoster!; }
    set noktaGoster(deger: boolean) {
        this._ayarlar.noktaGoster = deger;
        this.noktaMeshleriniGuncelle();
    }

    /** En üstte */
    get enUstte(): boolean { return this._ayarlar.enUstte!; }
    set enUstte(deger: boolean) {
        this._ayarlar.enUstte = deger;
        this.depthAyarla();
    }

    /** Render et */
    get renderEt(): boolean { return this._ayarlar.renderEt!; }
    set renderEt(deger: boolean) {
        this._ayarlar.renderEt = deger;
        this.visible = deger;
    }

    // ========================================================================
    // CONSTRUCTOR
    // ========================================================================

    constructor(sahne?: FikoSahne, isim?: string) {
        // İsim verilmemişse otomatik sayaçlı isim ata
        if (!isim || isim.trim() === '') {
            isim = `Polyline.${FikoYT3dPolyline.SAYAC++}`;
        }
        super(sahne, isim);

        // Simge ve element tipi ayarla
        this.simge = '/simgeler/polyline.png';
        this.userData['FikoArayuz'].elementTip = 'Polyline';
        this.etiket = this.name;

        // Varsayılan 3 nokta ile başla (üçgen şekli)
        this._noktalar = [
            new THREE.Vector3(0, 0, 0),
            new THREE.Vector3(50, 0, 0),
            new THREE.Vector3(25, 50, 0)
        ];

        // Çizgiyi oluştur
        this.cizgiyiOlustur();

        // Sağ tıklama menüsü
        this.sagMenuOlustur();
    }

    // ========================================================================
    // OLUŞTURMA METODLARİ
    // ========================================================================

    /**
     * Noktalardan polyline oluştur
     */
    noktalarileBelirle(noktalar: THREE.Vector3[], kapali: boolean = false): void {
        if (noktalar.length < 2) {
            console.warn('[FikoYT3dPolyline] En az 2 nokta gerekli');
            return;
        }

        this._noktalar = noktalar.map(n => n.clone());
        this._ayarlar.kapali = kapali;
        this.cizgiyiGuncelle();
    }

    /**
     * Düzenli çokgen oluştur (daire üzerinde eşit aralıklı noktalar)
     */
    duzenliCokgenOlustur(merkez: THREE.Vector3, yaricap: number, kenarSayisi: number): void {
        if (kenarSayisi < 3) {
            console.warn('[FikoYT3dPolyline] Çokgen için en az 3 kenar gerekli');
            return;
        }

        this._noktalar = [];
        const aciAdim = (Math.PI * 2) / kenarSayisi;

        for (let i = 0; i < kenarSayisi; i++) {
            const aci = aciAdim * i - Math.PI / 2;  // Üstten başla
            const x = merkez.x + Math.cos(aci) * yaricap;
            const y = merkez.y + Math.sin(aci) * yaricap;
            const z = merkez.z;
            this._noktalar.push(new THREE.Vector3(x, y, z));
        }

        this._ayarlar.kapali = true;
        this.cizgiyiGuncelle();
    }

    /**
     * Dikdörtgen oluştur
     */
    dikdortgenOlustur(merkez: THREE.Vector3, genislik: number, yukseklik: number): void {
        const yarimG = genislik / 2;
        const yarimY = yukseklik / 2;

        this._noktalar = [
            new THREE.Vector3(merkez.x - yarimG, merkez.y - yarimY, merkez.z),
            new THREE.Vector3(merkez.x + yarimG, merkez.y - yarimY, merkez.z),
            new THREE.Vector3(merkez.x + yarimG, merkez.y + yarimY, merkez.z),
            new THREE.Vector3(merkez.x - yarimG, merkez.y + yarimY, merkez.z)
        ];

        this._ayarlar.kapali = true;
        this.cizgiyiGuncelle();
    }

    /**
     * Yıldız şekli oluştur
     */
    yildizOlustur(merkez: THREE.Vector3, disYaricap: number, icYaricap: number, kolSayisi: number): void {
        if (kolSayisi < 3) {
            console.warn('[FikoYT3dPolyline] Yıldız için en az 3 kol gerekli');
            return;
        }

        this._noktalar = [];
        const noktaSayisi = kolSayisi * 2;
        const aciAdim = (Math.PI * 2) / noktaSayisi;

        for (let i = 0; i < noktaSayisi; i++) {
            const aci = aciAdim * i - Math.PI / 2;
            const yaricap = i % 2 === 0 ? disYaricap : icYaricap;
            const x = merkez.x + Math.cos(aci) * yaricap;
            const y = merkez.y + Math.sin(aci) * yaricap;
            this._noktalar.push(new THREE.Vector3(x, y, merkez.z));
        }

        this._ayarlar.kapali = true;
        this.cizgiyiGuncelle();
    }

    // ========================================================================
    // NOKTA YÖNETİMİ
    // ========================================================================

    /**
     * Nokta ekle (sona)
     */
    noktaEkle(nokta: THREE.Vector3): void {
        this._noktalar.push(nokta.clone());
        this.cizgiyiGuncelle();
    }

    /**
     * Belirli bir indekse nokta ekle
     */
    noktaEkleIndex(index: number, nokta: THREE.Vector3): void {
        if (index < 0 || index > this._noktalar.length) {
            console.warn('[FikoYT3dPolyline] Geçersiz index');
            return;
        }
        this._noktalar.splice(index, 0, nokta.clone());
        this.cizgiyiGuncelle();
    }

    /**
     * Nokta sil
     */
    noktaSil(index: number): void {
        if (index < 0 || index >= this._noktalar.length) {
            console.warn('[FikoYT3dPolyline] Geçersiz index');
            return;
        }
        if (this._noktalar.length <= 2) {
            console.warn('[FikoYT3dPolyline] En az 2 nokta kalmalı');
            return;
        }
        this._noktalar.splice(index, 1);
        this.cizgiyiGuncelle();
    }

    /**
     * Nokta pozisyonunu güncelle
     */
    noktaGuncelle(index: number, yeniPozisyon: THREE.Vector3): void {
        if (index < 0 || index >= this._noktalar.length) {
            console.warn('[FikoYT3dPolyline] Geçersiz index');
            return;
        }
        this._noktalar[index].copy(yeniPozisyon);
        this.cizgiyiGuncelle();
    }

    /**
     * Tüm noktaları taşı (translate)
     */
    tasi(delta: THREE.Vector3): void {
        this._noktalar.forEach(n => n.add(delta));
        this.cizgiyiGuncelle();
    }

    /**
     * Merkez etrafında döndür
     */
    dondur(aci: number, eksen: THREE.Vector3 = new THREE.Vector3(0, 0, 1)): void {
        const merkez = this.merkezNokta;
        const quaternion = new THREE.Quaternion().setFromAxisAngle(eksen.normalize(), aci);

        this._noktalar.forEach(n => {
            n.sub(merkez);
            n.applyQuaternion(quaternion);
            n.add(merkez);
        });

        this.cizgiyiGuncelle();
    }

    /**
     * Ölçekle
     */
    olcekle(faktor: number, merkez?: THREE.Vector3): void {
        const pivot = merkez || this.merkezNokta;

        this._noktalar.forEach(n => {
            n.sub(pivot);
            n.multiplyScalar(faktor);
            n.add(pivot);
        });

        this.cizgiyiGuncelle();
    }

    /**
     * İki nokta arasına ortaya yeni nokta ekle
     */
    segmentiBol(segmentIndex: number): void {
        if (segmentIndex < 0 || segmentIndex >= this.segmentSayisi) {
            console.warn('[FikoYT3dPolyline] Geçersiz segment index');
            return;
        }

        const p1 = this._noktalar[segmentIndex];
        const p2Index = (segmentIndex + 1) % this._noktalar.length;
        const p2 = this._noktalar[p2Index];

        const ortaNokta = new THREE.Vector3().addVectors(p1, p2).multiplyScalar(0.5);
        this._noktalar.splice(segmentIndex + 1, 0, ortaNokta);
        this.cizgiyiGuncelle();
    }

    // ========================================================================
    // ÇİZGİ OLUŞTURMA
    // ========================================================================

    /**
     * Çizgiyi oluştur
     */
    private cizgiyiOlustur(): void {
        if (this._noktalar.length < 2) return;

        // Geometri oluştur
        this.geometriOlustur();

        // Materyal oluştur
        this.materyalOlustur();

        // Çizgi mesh'i oluştur
        this.cizgiMeshiOlustur();

        // Nokta mesh'leri
        if (this._ayarlar.noktaGoster) {
            this.noktaMeshleriniOlustur();
        }
    }

    /**
     * Geometri oluştur
     */
    private geometriOlustur(): void {
        const noktalar = this.cizgiNoktalariniHazirla();

        if (this._ayarlar.cizgiTipi === 'Line2') {
            // Line2 için flat array (x, y, z, x, y, z, ...)
            const positions: number[] = [];
            noktalar.forEach(n => positions.push(n.x, n.y, n.z));

            this._geometri = new LineGeometry();
            (this._geometri as LineGeometry).setPositions(positions);
        } else {
            // Standard Line/LineSegments için BufferGeometry
            this._geometri = new THREE.BufferGeometry().setFromPoints(noktalar);
        }
    }

    /**
     * Çizgi noktalarını hazırla (kapalı ise son noktayı ekle)
     */
    private cizgiNoktalariniHazirla(): THREE.Vector3[] {
        const noktalar = [...this._noktalar];
        if (this._ayarlar.kapali && noktalar.length > 2) {
            noktalar.push(noktalar[0].clone());
        }
        return noktalar;
    }

    /**
     * Materyal oluştur
     */
    private materyalOlustur(): void {
        const renkDeger = typeof this._ayarlar.renk === 'string'
            ? new THREE.Color(this._ayarlar.renk).getHex()
            : this._ayarlar.renk!;

        if (this._ayarlar.cizgiTipi === 'Line2') {
            // Resolution al - sahne renderer'ından veya varsayılan
            let resolutionX = 1920;
            let resolutionY = 1080;

            try {
                if (this.sahne?.anaGosterim?.renderer) {
                    const renderer = this.sahne.anaGosterim.renderer as unknown as THREE.WebGLRenderer;
                    if (renderer.getSize) {
                        const size = renderer.getSize(new THREE.Vector2());
                        resolutionX = size.x || 1920;
                        resolutionY = size.y || 1080;
                    }
                }
            } catch {
                // Varsayılan değerleri kullan
            }

            this._materyal = new LineMaterial({
                color: renkDeger,
                linewidth: this._ayarlar.kalinlik!,  // Pixel cinsinden (Line2 doğrudan pixel kullanır)
                transparent: true,
                opacity: this._ayarlar.opaklık!,
                depthTest: !this._ayarlar.enUstte,
                depthWrite: !this._ayarlar.enUstte,
                alphaToCoverage: true,
                resolution: new THREE.Vector2(resolutionX, resolutionY)
            });

            // Kesikli stil
            if (this._ayarlar.stil === 'kesikli' || this._ayarlar.stil === 'kesikli-noktali') {
                (this._materyal as LineMaterial).dashed = true;
                (this._materyal as LineMaterial).dashScale = 1;
                (this._materyal as LineMaterial).dashSize = this._ayarlar.kesikliBoyut!;
                (this._materyal as LineMaterial).gapSize = this._ayarlar.kesikliAralik!;
            }
        } else {
            // Standard material
            if (this._ayarlar.stil === 'duz') {
                this._materyal = new THREE.LineBasicMaterial({
                    color: renkDeger,
                    transparent: true,
                    opacity: this._ayarlar.opaklık!,
                    depthTest: !this._ayarlar.enUstte,
                    depthWrite: !this._ayarlar.enUstte
                });
            } else {
                this._materyal = new THREE.LineDashedMaterial({
                    color: renkDeger,
                    transparent: true,
                    opacity: this._ayarlar.opaklık!,
                    depthTest: !this._ayarlar.enUstte,
                    depthWrite: !this._ayarlar.enUstte,
                    dashSize: this._ayarlar.kesikliBoyut!,
                    gapSize: this._ayarlar.kesikliAralik!
                });
            }
        }
    }

    /**
     * Çizgi mesh'i oluştur
     */
    private cizgiMeshiOlustur(): void {
        if (!this._geometri || !this._materyal) return;

        // Mevcut çizgiyi temizle
        if (this._cizgi) {
            this.remove(this._cizgi);
            this._cizgi.geometry.dispose();
        }

        if (this._ayarlar.cizgiTipi === 'Line2') {
            this._cizgi = new Line2(
                this._geometri as LineGeometry,
                this._materyal as LineMaterial
            );
            (this._cizgi as Line2).computeLineDistances();
        } else if (this._ayarlar.cizgiTipi === 'LineSegments') {
            this._cizgi = new THREE.LineSegments(
                this._geometri as THREE.BufferGeometry,
                this._materyal as THREE.LineBasicMaterial | THREE.LineDashedMaterial
            );
        } else {
            this._cizgi = new THREE.Line(
                this._geometri as THREE.BufferGeometry,
                this._materyal as THREE.LineBasicMaterial | THREE.LineDashedMaterial
            );
        }

        // UserData
        this._cizgi.userData['elementTipi'] = 'polyline';
        this._cizgi.userData['ustElement'] = this;
        this._cizgi.userData['secilebilir'] = this._ayarlar.secilebilir;

        // ciftTiklandi - FikoSecililer bu fonksiyonu çağırır
        const polylineRef = this;
        Object.defineProperty(this._cizgi, 'ciftTiklandi', {
            value: function($event?: any) { return polylineRef.ciftTiklandi($event); },
            writable: true,
            configurable: true
        });

        // sagMenu - sağ tıklama menüsü
        Object.defineProperty(this._cizgi, 'sagMenu', {
            get: function() { return polylineRef.sagMenu; },
            configurable: true
        });

        // Çizgiyi ekle
        this.add(this._cizgi);
    }

    /**
     * Nokta mesh'lerini oluştur
     * NOT: Artık THREE.Points kullanılıyor (FikoYT3dPolylineHandles)
     * Bu metod devre dışı - mesh'ler yerine Points ile sabit boyutlu noktalar gösteriliyor
     */
    private noktaMeshleriniOlustur(): void {
        // Mesh'ler yerine Points kullanılıyor - bu metod artık boş
        // Handle'lar (Points) ayrı olarak yönetiliyor
        return;
    }

    /**
     * Nokta mesh'lerini temizle
     */
    private noktaMeshleriniTemizle(): void {
        this._noktaMeshleri.forEach(mesh => {
            this.remove(mesh);
            mesh.geometry.dispose();
            (mesh.material as THREE.Material).dispose();
        });
        this._noktaMeshleri = [];
    }

    /**
     * Nokta mesh'lerini güncelle
     */
    private noktaMeshleriniGuncelle(): void {
        if (this._ayarlar.noktaGoster) {
            this.noktaMeshleriniOlustur();
        } else {
            this.noktaMeshleriniTemizle();
        }
    }

    // ========================================================================
    // GÜNCELLEME METODLARİ
    // ========================================================================

    /** Son güncelleme sırasındaki nokta sayısı */
    private _sonNoktaSayisi: number = 0;

    /**
     * Çizgiyi güncelle
     */
    private cizgiyiGuncelle(): void {
        if (this._noktalar.length < 2) return;

        const noktalar = this.cizgiNoktalariniHazirla();

        // Nokta sayısı değiştiyse geometry'yi yeniden oluştur
        if (noktalar.length !== this._sonNoktaSayisi) {
            this._sonNoktaSayisi = noktalar.length;
            this.cizgiyiYenidenOlustur();
            return;
        }

        if (this._ayarlar.cizgiTipi === 'Line2' && this._geometri instanceof LineGeometry) {
            const positions: number[] = [];
            noktalar.forEach(n => positions.push(n.x, n.y, n.z));
            this._geometri.setPositions(positions);
            if (this._cizgi instanceof Line2) {
                this._cizgi.computeLineDistances();
            }
        } else if (this._geometri instanceof THREE.BufferGeometry) {
            this._geometri.setFromPoints(noktalar);
        }

        // Nokta mesh'lerini güncelle
        if (this._ayarlar.noktaGoster) {
            this.noktaMeshleriniOlustur();
        }

        // Handle'ları güncelle
        if (this._handles) {
            this._handles.guncelle();
        }

        // Değişiklik callback'i
        if (this.olaylar?.degisti) {
            this.olaylar.degisti();
        }
    }

    /**
     * Çizgiyi yeniden oluştur
     */
    private cizgiyiYenidenOlustur(): void {
        // Mevcut çizgiyi temizle
        if (this._cizgi) {
            this.remove(this._cizgi);
            if (this._geometri) this._geometri.dispose();
            if (this._materyal) (this._materyal as THREE.Material).dispose();
        }

        // Yeniden oluştur
        this.cizgiyiOlustur();
    }

    /**
     * Materyal güncelle
     */
    private materyaliGuncelle(): void {
        if (!this._materyal) return;

        const renkDeger = typeof this._ayarlar.renk === 'string'
            ? new THREE.Color(this._ayarlar.renk).getHex()
            : this._ayarlar.renk!;

        if (this._materyal instanceof LineMaterial) {
            this._materyal.color.setHex(renkDeger);
            this._materyal.linewidth = this._ayarlar.kalinlik!;  // Pixel cinsinden
            this._materyal.opacity = this._ayarlar.opaklık!;
            this._materyal.needsUpdate = true;
        } else {
            (this._materyal as THREE.LineBasicMaterial).color.setHex(renkDeger);
            this._materyal.opacity = this._ayarlar.opaklık!;
            this._materyal.needsUpdate = true;
        }
    }

    /**
     * Stil güncelle
     */
    private stilGuncelle(): void {
        // Stil değişikliği çizginin yeniden oluşturulmasını gerektirir
        this.cizgiyiYenidenOlustur();
    }

    /**
     * Depth ayarla
     */
    private depthAyarla(): void {
        if (this._materyal) {
            this._materyal.depthTest = !this._ayarlar.enUstte;
            this._materyal.depthWrite = !this._ayarlar.enUstte;
            this._materyal.needsUpdate = true;
        }
    }

    /**
     * Materyali tazele (resolution güncelle)
     * Dialog veya ilk render için çağırılmalı
     */
    materyaliTazele(): void {
        if (!(this._materyal instanceof LineMaterial)) return;

        // Resolution'ı güncelle
        let resolutionX = 1920;
        let resolutionY = 1080;

        try {
            if (this.sahne?.anaGosterim?.renderer) {
                const renderer = this.sahne.anaGosterim.renderer as unknown as THREE.WebGLRenderer;
                if (renderer.getSize) {
                    const size = renderer.getSize(new THREE.Vector2());
                    resolutionX = size.x || 1920;
                    resolutionY = size.y || 1080;
                }
            }
        } catch {
            // Varsayılan değerleri kullan
        }

        this._materyal.resolution.set(resolutionX, resolutionY);
        this._materyal.linewidth = this._ayarlar.kalinlik!;
        this._materyal.needsUpdate = true;

        // Geometriyi de güncelle
        if (this._cizgi instanceof Line2) {
            this._cizgi.computeLineDistances();
        }
    }

    // ========================================================================
    // AYARLAR
    // ========================================================================

    /**
     * Ayarları belirle
     */
    ayarlariBelirle(ayarlar: IPolylineAyarlari): void {
        this._ayarlar = { ...VARSAYILAN_AYARLAR, ...ayarlar };
        this.cizgiyiYenidenOlustur();
    }

    /**
     * Mevcut ayarları al
     */
    ayarlariAl(): IPolylineAyarlari {
        return { ...this._ayarlar };
    }

    // ========================================================================
    // HANDLE SİSTEMİ
    // ========================================================================

    /**
     * Handle'ları göster
     */
    handlelerGoster(): FikoYT3dPolylineHandles {
        if (this._handles) {
            this._handles.guncelle();
        } else {
            this._handles = new FikoYT3dPolylineHandles(this);
        }

        // Sahneye ekle (polyline'ın parent'ına değil, doğrudan sahneye)
        if (this.sahne && !this._handles.parent) {
            this.sahne.add(this._handles);
        } else if (!this._handles.parent) {
            this.add(this._handles);
        }

        return this._handles;
    }

    /**
     * Handle'ları gizle
     */
    handlelerGizle(): void {
        if (this._handles) {
            if (this._handles.parent) {
                this._handles.parent.remove(this._handles);
            }
            this._handles.temizle();
            this._handles = null;
        }
    }

    /**
     * Handle referansını al
     */
    get handles(): FikoYT3dPolylineHandles | null {
        return this._handles;
    }

    // ========================================================================
    // SAĞ TIKLAMA MENÜSÜ
    // ========================================================================

    /**
     * Sağ tıklama menüsünü oluştur
     */
    private sagMenuOlustur(): void {
        this.sagMenu = [
            {
                simgeYazi: '👁‍🗨',
                baslik: 'Gizle / Göster',
                tiklandi: () => {
                    this.visible = !this.visible;
                }
            },
            {
                simgeYazi: '🖊',
                baslik: 'Düzenle',
                tiklandi: () => this.ciftTiklandi()
            },
            {
                simgeYazi: '➕',
                baslik: 'Nokta Ekle',
                tiklandi: () => {
                    // Son iki nokta arasına ekle
                    if (this._noktalar.length >= 2) {
                        this.segmentiBol(this._noktalar.length - 2);
                    }
                }
            },
            {
                tipi: 'Ayraç'
            },
            {
                simgeYazi: this.kapali ? '🔓' : '🔒',
                baslik: 'Kapat/Aç',
                tiklandi: () => {
                    this.kapali = !this.kapali;
                    // Menüyü güncelle
                    this.sagMenuOlustur();
                }
            },
            {
                simgeYazi: '🎯',
                baslik: 'Ekrana Sığdır',
                tiklandi: () => this.ekranaSigdir()
            },
            {
                tipi: 'Ayraç'
            },
            {
                simgeYazi: '📋',
                baslik: 'Kopyala',
                tiklandi: () => this.kopyala()
            },
            {
                simgeYazi: '🗑',
                baslik: 'Sil',
                tiklandi: () => this.sil()
            }
        ];
    }

    // ========================================================================
    // ÇIFT TIKLAMA (Dialog aç)
    // ========================================================================

    /** Bu polyline için açık dialog referansı */
    private _acikDialog?: any;

    /** Duplicate dialog açmayı önlemek için kilit */
    private _dialogAciliyor = false;

    /** Static: Herhangi bir polyline dialog'u açık mı? */
    private static _globalAcikDialog?: any;

    /** Static: Dialog component referansı (WeakRef ile) */
    private static _dialogComponentRef?: WeakRef<any>;

    /** Static: Global dialog'u ayarla (dialog component'ten çağrılır) */
    static globalDialogAyarla(dialog: any): void {
        FikoYT3dPolyline._globalAcikDialog = dialog;
        // Component referansını da kaydet (pencere üzerinden)
        if (dialog?.pencere?.bilesenInstance) {
            FikoYT3dPolyline._dialogComponentRef = new WeakRef(dialog.pencere.bilesenInstance);
        }
    }

    /** Static: Global dialog'u temizle */
    static globalDialogTemizle(): void {
        FikoYT3dPolyline._globalAcikDialog = undefined;
        FikoYT3dPolyline._dialogComponentRef = undefined;
    }

    /** Static: Global dialog gerçekten aktif mi kontrol et */
    static globalDialogAktifMi(): boolean {
        // WeakRef kontrolü - component garbage collected olduysa false döner
        if (FikoYT3dPolyline._dialogComponentRef) {
            const componentRef = FikoYT3dPolyline._dialogComponentRef.deref();
            if (!componentRef) {
                // Component artık yok, temizle
                FikoYT3dPolyline.globalDialogTemizle();
                return false;
            }
        }

        // Global dialog referansı kontrolü
        if (!FikoYT3dPolyline._globalAcikDialog) {
            return false;
        }

        const globalDialog = FikoYT3dPolyline._globalAcikDialog;

        // Dialog durumu kontrolü
        if (!globalDialog.gorunur && !globalDialog.miAcik) {
            FikoYT3dPolyline.globalDialogTemizle();
            return false;
        }

        return true;
    }

    /**
     * Çift tıklama - Düzenleme dialogunu aç
     */
    async ciftTiklandi($event?: any): Promise<void> {
        // Eğer dialog açma işlemi devam ediyorsa, tekrar çağırmayı engelle
        if (this._dialogAciliyor) {
            return;
        }

        if (!this.sahne?.anaGosterim?.DialogFiko) {
            return;
        }

        const dialogId = 'fiko-3d-polyline-edit-' + this.uuid;
        const dialogService = this.sahne.anaGosterim.DialogFiko;

        // 0. Global açık dialog kontrolü (WeakRef + durum kontrolü)
        if (FikoYT3dPolyline.globalDialogAktifMi()) {
            const globalDialog = FikoYT3dPolyline._globalAcikDialog;
            console.log('[FikoYT3dPolyline] Polyline dialog zaten açık, yeni dialog açılmıyor');
            // Mevcut dialog'u öne getir
            if (globalDialog?.id_dialog) {
                this.dialoguOneGetir(globalDialog, dialogService);
            }
            return;
        }

        // 1. Önce _acikDialog referansını kontrol et
        if (this._acikDialog && this._acikDialog.miAcik) {
            this.dialoguOneGetir(this._acikDialog, dialogService);
            return;
        }

        // 2. DialogFiko servisinde aynı ID'li dialog var mı kontrol et
        const mevcutDialog = dialogService.idIleAl?.(dialogId);
        if (mevcutDialog && mevcutDialog.miAcik) {
            this.dialoguOneGetir(mevcutDialog, dialogService);
            this._acikDialog = mevcutDialog;
            return;
        }

        // 4. Referansı temizle
        this._acikDialog = undefined;

        // 5. Kilitle
        this._dialogAciliyor = true;

        try {
            // 6. Dialog component'i lazy import et
            const { FikoYT3dPolylineDialogComponent } = await import('./fikoyt-3d-polyline-dialog.component');

            // 7. Unique dialog ID ile aç
            const dialog = dialogService.ac({
                bilesen: FikoYT3dPolylineDialogComponent,
                ayarlar: { dialogId },
                parametreler: {
                    sahne: this.sahne,
                    mevcutPolyline: this,
                    duzenleModu: true
                },
                $event
            });

            // 8. Referansları kaydet
            this._acikDialog = dialog;
            FikoYT3dPolyline._globalAcikDialog = dialog;

        } catch (error) {
            console.error('[FikoYT3dPolyline] Dialog import hatası:', error);
        } finally {
            this._dialogAciliyor = false;
        }
    }

    /**
     * Dialog'u öne getir
     */
    private dialoguOneGetir(dialog: any, dialogService: any): void {
        const dialogEl = document.getElementById(dialog.id_dialog);
        if (dialogEl) {
            const yeniZIndex = dialogService.overlayZIndexAl?.() || 99999;
            dialogEl.style.setProperty('z-index', yeniZIndex.toString(), 'important');
            const parent = dialogEl.parentElement;
            if (parent) {
                parent.style.setProperty('z-index', yeniZIndex.toString(), 'important');
            }
            dialogService.dialogTiklandi?.(dialog);
            dialogEl.focus();
        }
    }

    // ========================================================================
    // SERİLEŞTİRME
    // ========================================================================

    /**
     * JSON'a dönüştür
     */
    override toJSON(): any {
        return {
            ...super.toJSON(),
            type: 'FikoYT3dPolyline',
            noktalar: this._noktalar.map(n => ({ x: n.x, y: n.y, z: n.z })),
            ayarlar: { ...this._ayarlar }
        };
    }

    /**
     * JSON'dan oluştur
     */
    static fromJSON(json: any, sahne?: FikoSahne): FikoYT3dPolyline {
        const polyline = new FikoYT3dPolyline(sahne, json.name);

        if (json.noktalar) {
            polyline._noktalar = json.noktalar.map((n: any) =>
                new THREE.Vector3(n.x, n.y, n.z)
            );
        }

        if (json.ayarlar) {
            polyline._ayarlar = { ...VARSAYILAN_AYARLAR, ...json.ayarlar };
        }

        polyline.cizgiyiYenidenOlustur();
        return polyline;
    }

    // ========================================================================
    // YAŞAM DÖNGÜSÜ
    // ========================================================================

    /**
     * Her frame güncelleme
     */
    override guncelle(deltaTime?: number, kamera?: THREE.Camera): void {
        super.guncelle(deltaTime, kamera);

        // Line2 için resolution güncelle
        if (this._materyal instanceof LineMaterial && this.sahne?.anaGosterim?.renderer) {
            const renderer = this.sahne.anaGosterim.renderer as unknown as THREE.WebGLRenderer;
            if (renderer.getSize) {
                const size = renderer.getSize(new THREE.Vector2());
                this._materyal.resolution.set(size.x, size.y);
            }
        }
    }

    /**
     * Polyline'ı ekrana sığdır (kamerayı polyline'a odakla)
     */
    ekranaSigdir(): void {
        const anaGosterim = this.sahne?.anaGosterim as any;
        if (!anaGosterim) {
            console.warn('[FikoYT3dPolyline] Ekrana sığdırma için anaGosterim bulunamadı');
            return;
        }

        // Bounding box'ı tüm noktalardan hesapla
        const box = new THREE.Box3();
        this._noktalar.forEach(nokta => box.expandByPoint(nokta));

        // Padding ekle
        const size = box.getSize(new THREE.Vector3());
        const padding = Math.max(size.length() * 0.5, 50);
        box.expandByScalar(padding);

        // Merkez ve yarıçap
        const center = box.getCenter(new THREE.Vector3());
        const radius = box.getSize(new THREE.Vector3()).length() / 2;

        // Kamera ve kontrol
        const kamera = anaGosterim.aktifKamera;
        const kontrol = anaGosterim.aktifKontrol;

        if (!kamera || !kontrol) {
            console.warn('[FikoYT3dPolyline] Kamera veya kontrol bulunamadı');
            return;
        }

        // Canvas boyutları
        const canvas = anaGosterim.canvas?.nativeElement;
        if (!canvas || canvas.clientWidth === 0) {
            console.warn('[FikoYT3dPolyline] Canvas bulunamadı');
            return;
        }

        const aspect = canvas.clientWidth / canvas.clientHeight;

        // TWEEN ile animasyonlu geçiş
        if (typeof TWEEN !== 'undefined') {
            anaGosterim.surekliCiz?.();

            // Yeni zoom hesapla
            const yakinlik = (aspect > 1.0)
                ? canvas.clientHeight / (radius * 2)
                : canvas.clientWidth / (radius * 2);

            // Kamera pozisyonunu merkeze göre ayarla
            const oncekiTarget = kontrol.target.clone();
            const kameraOffset = kamera.position.clone().sub(oncekiTarget);
            const kameraYeniPoz = center.clone().add(kameraOffset);

            // Kamera animasyonu
            new TWEEN.Tween({
                x: kamera.position.x,
                y: kamera.position.y,
                z: kamera.position.z,
                zoom: kamera.zoom
            })
            .to({
                x: kameraYeniPoz.x,
                y: kameraYeniPoz.y,
                z: kameraYeniPoz.z,
                zoom: yakinlik
            }, 400)
            .easing(TWEEN.Easing.Quadratic.Out)
            .onUpdate((val: any) => {
                kamera.position.set(val.x, val.y, val.z);
                kamera.zoom = val.zoom;
                kamera.updateProjectionMatrix();
            })
            .onComplete(() => {
                anaGosterim.surekliCizIptal?.();
            })
            .start();

            // Target animasyonu
            new TWEEN.Tween({
                x: kontrol.target.x,
                y: kontrol.target.y,
                z: kontrol.target.z
            })
            .to({
                x: center.x,
                y: center.y,
                z: center.z
            }, 400)
            .easing(TWEEN.Easing.Quadratic.Out)
            .onUpdate((val: any) => {
                kontrol.target.set(val.x, val.y, val.z);
                kontrol.update();
            })
            .start();

        } else {
            // TWEEN yoksa direkt ayarla
            kontrol.target.copy(center);
            kamera.lookAt(center);
            kontrol.update();
        }
    }

    /**
     * Kopyala
     */
    kopyala(): FikoYT3dPolyline {
        const kopya = new FikoYT3dPolyline(this.sahne, this.name + '_kopya');
        kopya._noktalar = this._noktalar.map(n => n.clone());
        kopya._ayarlar = { ...this._ayarlar };
        kopya.cizgiyiYenidenOlustur();

        // Sahneye ekle
        if (this.sahne) {
            this.sahne.add(kopya);
        }

        return kopya;
    }

    /**
     * Sil
     */
    sil(): void {
        this.handlelerGizle();
        this.temizle();

        if (this.parent) {
            this.parent.remove(this);
        }
    }

    /**
     * Kaynakları temizle
     */
    override temizle(): void {
        // Handle'ları temizle
        this.handlelerGizle();

        // Çizgiyi temizle
        if (this._cizgi) {
            this.remove(this._cizgi);
            if (this._geometri) this._geometri.dispose();
            if (this._materyal) (this._materyal as THREE.Material).dispose();
        }

        // Nokta mesh'lerini temizle
        this.noktaMeshleriniTemizle();

        super.temizle();
    }
}

// ============================================================================
// YARDIMCI FONKSİYONLAR
// ============================================================================

/**
 * İki nokta arası mesafe
 */
export function noktalarArasiMesafe(p1: THREE.Vector3, p2: THREE.Vector3): number {
    return p1.distanceTo(p2);
}

/**
 * Polyline uzunluğu hesapla
 */
export function polylineUzunlugu(noktalar: THREE.Vector3[], kapali: boolean = false): number {
    let uzunluk = 0;
    for (let i = 0; i < noktalar.length - 1; i++) {
        uzunluk += noktalar[i].distanceTo(noktalar[i + 1]);
    }
    if (kapali && noktalar.length > 2) {
        uzunluk += noktalar[noktalar.length - 1].distanceTo(noktalar[0]);
    }
    return uzunluk;
}

/**
 * Polyline merkezi hesapla
 */
export function polylineMerkezi(noktalar: THREE.Vector3[]): THREE.Vector3 {
    const merkez = new THREE.Vector3();
    noktalar.forEach(n => merkez.add(n));
    merkez.divideScalar(noktalar.length);
    return merkez;
}
